import React, {useState, useEffect, useContext} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {UserContext} from '../../UserContext';
import {usePreventDoubleClick} from '../hooks/usePreventDoubleClick';
import {useHttpClient} from '../hooks/useHttpClient';
import {useNotificacoes} from '../hooks/useNotificacoes';

import Formulario from '../../components/ui/Formulario';
import BotaoSalvar from '../../components/BotaoSalvar';

import {isDataValida, formatarKwh10_2} from '../../util/validacoes';

import styles from './CadastroProducaoUsina.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

function CadastroProducaoUsina() {
    const {exibirErro} = useNotificacoes();
    const {id} = useParams();
    const {user} = useContext(UserContext);
    const navigate = useNavigate();
    const {get, post, put} = useHttpClient();

    const [usinas, setUsinas] = useState([]);
    const [formData, setFormData] = useState({
        idUsina: '',
        mesProducao: '',
        // agora temos dois campos para quantidade:
        qtdProducaoBruto: 0,   // número puro
        qtdProducao: '',       // string para exibição
        dtInicioProducao: '',
        dtFimProducao: '',
    });

    // 1. redireciona se não logado
    useEffect(() => {
        if (user === null) navigate('/login');
    }, [user, navigate]);

    // 2. carrega usinas
    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const data = await get(`/hello-sol/usinas/buscar?idEmpresa=${user.empresa.id}`);
                setUsinas(Array.isArray(data) ? data : data.content ?? []);
            } catch {
                exibirErro('Erro ao buscar usinas.');
            }
        })();
    }, [user]);

    // 3. se edição, carrega dados
    useEffect(() => {
        if (!user || !id) return;
        (async () => {
            try {
                const data = await get(`/hello-sol/producao-usinas/${id}`);

                const bruto = parseFloat(data.qtdProducao.replace(',', '.')) || 0;

                setFormData({
                    idUsina: data.usina.id,
                    mesProducao: String(data.mesProducao),
                    qtdProducaoBruto: bruto,
                    qtdProducao: formatarKwh10_2(String(bruto)),
                    dtInicioProducao: new Date(data.dtInicioProducao)
                        .toLocaleDateString('pt-BR', {timeZone: 'UTC'}),
                    dtFimProducao: new Date(data.dtFimProducao)
                        .toLocaleDateString('pt-BR', {timeZone: 'UTC'}),
                });

            } catch {
                exibirErro('Erro ao carregar produção.');
            }
        })();
    }, [user, id]);

    // manipula todos os campos, agora sem formatar qtd na digitação
    const handleChange = e => {
        const {name, value} = e.target;

        if (name === 'qtdProducao') {
            // mantemos só dígitos e vírgula enquanto digita
            const clean = value.replace(/[^\d,]/g, '');
            setFormData(f => ({
                ...f,
                qtdProducao: clean,
                qtdProducaoBruto: parseFloat(clean.replace(',', '.')) || 0
            }));
        } else {
            setFormData(f => ({...f, [name]: value}));
        }
    };

    const handleDateInput = (e, field) => {
        let v = e.target.value.replace(/\D/g, '');
        if (v.length > 2) v = v.replace(/^(\d{2})(\d)/, '$1/$2');
        if (v.length > 5) v = v.replace(/^(\d{2}\/\d{2})(\d)/, '$1/$2');
        setFormData(f => ({...f, [field]: v}));
    };

    const salvarProducao = async () => {
        try {
            const {dtInicioProducao, dtFimProducao, mesProducao, qtdProducaoBruto} = formData;

            // valida datas
            if (!isDataValida(dtInicioProducao)) {
                exibirErro('Data de início inválida. Utilize o formato dd/mm/aaaa e uma data real.');
                return;
            }
            if (!isDataValida(dtFimProducao)) {
                exibirErro('Data de fim inválida. Utilize o formato dd/mm/aaaa e uma data real.');
                return;
            }

            const [d1, m1, y1] = dtInicioProducao.split('/');
            const [d2, m2, y2] = dtFimProducao.split('/');
            const inicio = new Date(+y1, +m1 - 1, +d1);
            const fim = new Date(+y2, +m2 - 1, +d2);

            // ordem cronológica
            if (inicio > fim) {
                exibirErro('A data de início não pode ser posterior à data de fim.');
                return;
            }

            // mês de produção compatível
            //const mesEsperado = parseInt(mesProducao, 10);
            //if (inicio.getMonth() + 1 !== mesEsperado || fim.getMonth() + 1 !== mesEsperado) {
            //    exibirErro('O período informado deve estar dentro do mês de produção selecionado.');
            //   return;
            //}

            // monta payload
            const payload = {
                ...formData,
                qtdProducao: qtdProducaoBruto,                      // número puro
                dtInicioProducao: inicio.toISOString().slice(0, 10),
                dtFimProducao: fim.toISOString().slice(0, 10),
            };

            if (id) {
                await put(`/hello-sol/producao-usinas/${id}`, payload);
            } else {
                await post(`/hello-sol/producao-usinas`, payload);
            }

            navigate('/home/listar-producao-usina');
        } catch (e) {
            exibirErro(e.message || 'Erro ao salvar produção.');
        }
    };

    const [safeSubmit, isSubmitting] = usePreventDoubleClick(salvarProducao);

    return (
        <div className={styles_global.container}>
            <h2>
                {id ? 'Alterar Produção de Usina' : 'Cadastrar Produção de Usina'}
            </h2>

            <Formulario onSubmit={safeSubmit}>
                <div className={styles.linha2}>
                    <div className={styles_content.formField}>
                        <label htmlFor="idUsina">Usina*</label>
                        <select
                            id="idUsina"
                            name="idUsina"
                            value={formData.idUsina}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Selecionar Usina</option>
                            {usinas.map(u => (
                                <option key={u.id} value={u.id}>
                                    {u.nome}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="mesProducao">Mês de Produção*</label>
                        <select
                            id="mesProducao"
                            name="mesProducao"
                            value={formData.mesProducao}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Selecionar Mês</option>
                            {[...Array(12)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>
                                    {new Date(0, i).toLocaleString('pt-BR', {month: 'long'}).replace(/^\w/, c => c.toUpperCase())}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.linha3}>
                    <div className={styles_content.formField}>
                        <label htmlFor="qtdProducao">Quantidade KWh*</label>
                        <input
                            id="qtdProducao"
                            name="qtdProducao"
                            value={formData.qtdProducao}
                            onChange={handleChange}
                            placeholder={formData.qtdProducao ? undefined : '0,00'}
                            maxLength={12}
                            onBlur={() => {
                                const bruto = formData.qtdProducaoBruto;
                                setFormData(f => ({
                                    ...f,
                                    qtdProducao: formatarKwh10_2(bruto)
                                }));
                            }}
                            required
                        />
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="dtInicioProducao">Data Início*</label>
                        <input
                            id="dtInicioProducao"
                            name="dtInicioProducao"
                            placeholder="dd/mm/aaaa"
                            maxLength={10}
                            value={formData.dtInicioProducao}
                            onChange={e => handleDateInput(e, 'dtInicioProducao')}
                            required
                        />
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="dtFimProducao">Data Fim*</label>
                        <input
                            id="dtFimProducao"
                            name="dtFimProducao"
                            placeholder="dd/mm/aaaa"
                            maxLength={10}
                            value={formData.dtFimProducao}
                            onChange={e => handleDateInput(e, 'dtFimProducao')}
                            required
                        />
                    </div>
                </div>

                <div className={styles_content.buttonGroup}>
                    <BotaoSalvar loading={isSubmitting} onClick={safeSubmit}/>
                </div>
            </Formulario>
        </div>
    );
}

export default CadastroProducaoUsina;
